// src/stores/progressStore.ts
// Zustand store for progress data with real-time sync

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface WeightEntry {
  id: string;
  user_id: string;
  weight: number;
  date: string;
  note?: string | null;
  photo_url?: string | null;
  created_at: string;
}

interface ProgressPhoto {
  id: string;
  user_id: string;
  photo_url: string;
  date: string;
  weight?: number | null;
  note?: string | null;
  created_at: string;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: string;
  color: string;
  requirement_type: string;
  requirement_value: number;
  order: number;
  is_active: boolean;
  details: string | null;
  tips: string | null;
}

interface UserAchievement {
  id: string;
  user_id: string;
  achievement_id: string;
  unlocked_at: string;
  achievement?: Achievement;
}

interface ProgressStats {
  current_weight: number;
  target_weight: number;
  weight_lost: number;
  current_streak: number;
  longest_streak: number;
  total_active_days: number;
  plan_adherence: number;
  total_weight_entries: number;
  total_photos: number;
}

interface ProgressState {
  // Data
  weightHistory: WeightEntry[];
  progressPhotos: ProgressPhoto[];
  achievements: Achievement[];
  userAchievements: UserAchievement[];
  stats: ProgressStats | null;
  
  // Loading states
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;

  // Actions - Weight History
  setWeightHistory: (history: WeightEntry[]) => void;
  addWeightEntry: (entry: WeightEntry) => void;
  updateWeightEntry: (id: string, updates: Partial<WeightEntry>) => void;
  deleteWeightEntry: (id: string) => void;
  
  // Actions - Photos
  setProgressPhotos: (photos: ProgressPhoto[]) => void;
  addProgressPhoto: (photo: ProgressPhoto) => void;
  deleteProgressPhoto: (id: string) => void;
  
  // Actions - Achievements
  setAchievements: (achievements: Achievement[]) => void;
  setUserAchievements: (userAchievements: UserAchievement[]) => void;
  addUserAchievement: (achievement: UserAchievement) => void;
  
  // Actions - Stats
  setStats: (stats: ProgressStats) => void;
  
  // Actions - Loading
  setIsLoading: (loading: boolean) => void;
  setIsSubmitting: (submitting: boolean) => void;
  setError: (error: string | null) => void;
  
  // Actions - Reset
  reset: () => void;
}

const initialState = {
  weightHistory: [],
  progressPhotos: [],
  achievements: [],
  userAchievements: [],
  stats: null,
  isLoading: false,
  isSubmitting: false,
  error: null,
};

export const useProgressStore = create<ProgressState>()(
  persist(
    (set) => ({
      ...initialState,

      // Weight History Actions
      setWeightHistory: (history) => 
        set({ 
          weightHistory: [...history].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )
        }),

      addWeightEntry: (entry) =>
        set((state) => {
          const newHistory = [entry, ...state.weightHistory];
          // Sort by created_at descending (newest first)
          return {
            weightHistory: newHistory.sort(
              (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            ),
          };
        }),

      updateWeightEntry: (id, updates) =>
        set((state) => ({
          weightHistory: state.weightHistory.map((entry) =>
            entry.id === id ? { ...entry, ...updates } : entry
          ),
        })),

      deleteWeightEntry: (id) =>
        set((state) => ({
          weightHistory: state.weightHistory.filter((entry) => entry.id !== id),
        })),

      // Progress Photos Actions
      setProgressPhotos: (photos) =>
        set({ progressPhotos: photos }),

      addProgressPhoto: (photo) =>
        set((state) => ({
          progressPhotos: [photo, ...state.progressPhotos].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          ),
        })),

      deleteProgressPhoto: (id) =>
        set((state) => ({
          progressPhotos: state.progressPhotos.filter((photo) => photo.id !== id),
        })),

      // Achievements Actions
      setAchievements: (achievements) =>
        set({ achievements }),

      setUserAchievements: (userAchievements) =>
        set({ userAchievements }),

      addUserAchievement: (achievement) =>
        set((state) => ({
          userAchievements: [...state.userAchievements, achievement],
        })),

      // Stats Actions
      setStats: (stats) =>
        set({ stats }),

      // Loading Actions
      setIsLoading: (loading) =>
        set({ isLoading: loading }),

      setIsSubmitting: (submitting) =>
        set({ isSubmitting: submitting }),

      setError: (error) =>
        set({ error }),

      // Reset
      reset: () => set(initialState),
    }),
    {
      name: 'progress-storage',
      partialize: (state) => ({
        weightHistory: state.weightHistory,
        progressPhotos: state.progressPhotos,
        achievements: state.achievements,
        userAchievements: state.userAchievements,
        stats: state.stats,
      }),
    }
  )
);